import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitStatus("success");
      return;
    }
    if (!email.trim()) return;

    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d8tnpukuatl81q3tfnd0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitStatus("success");
      setEmail("");
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50 pt-14 md:pt-20 pb-8">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 mb-12">
          <div className="w-full lg:w-[30%]">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-table-line text-background-50 text-base"></i>
              </div>
              <span className="font-heading text-lg font-bold">
                USFans Spreadsheet
              </span>
            </div>
            <p className="text-sm text-background-50/60 leading-relaxed mb-4">
              {t("footer.description")}
            </p>
          </div>

          <div className="w-full lg:w-[20%]">
            <h4 className="font-heading text-sm font-semibold mb-3 text-background-50">
              {t("footer.quickLinks")}
            </h4>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="text-sm text-background-50/50 hover:text-background-50 transition-colors text-left cursor-pointer whitespace-nowrap"
              >
                {t("breadcrumb.home")}
              </button>
              <button
                onClick={() => { navigate("/artykul/usfans-spreadsheet-przewodnik"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="text-sm text-background-50/50 hover:text-background-50 transition-colors text-left cursor-pointer whitespace-nowrap"
              >
                {t("nav.guide")}
              </button>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("nav.buyNow")}
              </a>
            </div>
          </div>

          <div className="w-full lg:w-[20%]">
            <h4 className="font-heading text-sm font-semibold mb-3 text-background-50">
              {t("footer.categories_footer")}
            </h4>
            <div className="flex flex-col gap-2">
              {categories.slice(0, 5).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/50 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-[30%]">
            <p className="text-sm text-background-50/70 mb-1">{t("footer.newsletter")}</p>
            <p className="text-xs text-background-50/50 mb-3">{t("footer.newsletterDesc")}</p>
            <form onSubmit={handleSubscribe} data-readdy-form className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="honeypot-field"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.emailPlaceholder")}
                required
                className="flex-1 px-4 py-2.5 rounded-full bg-foreground-800 border border-foreground-700 text-sm text-background-50 placeholder:text-background-50/30 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button
                type="submit"
                className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("footer.subscribe")}
                <i className="ri-arrow-right-line ml-1.5"></i>
              </button>
            </form>
            {submitStatus === "success" && (
              <p className="text-xs text-accent-400 mt-2">Dziękujemy za zapisanie się!</p>
            )}
            {submitStatus === "error" && (
              <p className="text-xs text-red-400 mt-2">Wystąpił błąd. Spróbuj ponownie.</p>
            )}
          </div>
        </div>

        <div className="border-t border-foreground-800 pt-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-center">
            <div className="w-full lg:w-[30%]">
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Curated%20lifestyle%20flat%20lay%20of%20fashion%20accessories%2C%20watch%2C%20wallet%2C%20scarf%20on%20light%20marble%20surface%2C%20warm%20soft%20lighting%2C%20cream%20background%2C%20editorial%20aesthetic%2C%20harmonious%20minimalist%20composition&width=600&height=450&seq=footer-lifestyle-new&orientation=landscape"
                  alt="USFans Spreadsheet produkty"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="w-full lg:w-[40%]">
              <p className="font-heading text-2xl md:text-3xl italic text-background-50/80 leading-tight">
                {t("footer.motto")}
              </p>
            </div>
            <div className="w-full lg:w-[30%] flex flex-col items-start lg:items-end gap-3">
              <p className="text-xs text-background-50/50">
                {t("footer.rights")}
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-background-50/40 hover:text-background-50/70 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-twitter-x-line"></i>
                  </div>
                </a>
                <a href="#" className="text-background-50/40 hover:text-background-50/70 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-facebook-line"></i>
                  </div>
                </a>
                <a href="#" className="text-background-50/40 hover:text-background-50/70 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-instagram-line"></i>
                  </div>
                </a>
                <a href="#" className="text-background-50/40 hover:text-background-50/70 transition-colors cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-linkedin-line"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}